import { useLocation, useParams } from 'react-router-dom'

export type VehicleDocumentType = 'invoice' | 'sell_contract' | 'import_document'

export type DocumentTypeLabelKey =
  | 'my_vehicles.document_invoice'
  | 'my_vehicles.document_sell_contract'
  | 'my_vehicles.document_import'

const documentTypeLabelKeys: Record<VehicleDocumentType, DocumentTypeLabelKey> = {
  invoice: 'my_vehicles.document_invoice',
  sell_contract: 'my_vehicles.document_sell_contract',
  import_document: 'my_vehicles.document_import'
}

export const isVehicleDocumentType = (documentType: string): documentType is VehicleDocumentType => {
  return documentType === 'invoice' || documentType === 'sell_contract' || documentType === 'import_document'
}

export const documentTypeLabelKey = (documentType: string): DocumentTypeLabelKey | null => {
  if (!isVehicleDocumentType(documentType)) {
    return null
  }
  return documentTypeLabelKeys[documentType]
}

interface DocumentCreatePathParams {
  documentType: VehicleDocumentType
  clientId?: string
  vehicleId?: string
}

const appendQueryParams = (path: string, params: URLSearchParams): string => {
  const query = params.toString()
  if (query.length === 0) {
    return path
  }
  return `${path}?${query}`
}

const optionalParam = (value: string | null | undefined): string => {
  if (value === null || typeof value === 'undefined' || value.length === 0) {
    return ''
  }
  return value
}

export const documentCreateBasePath = (documentType: VehicleDocumentType): string => {
  if (documentType === 'invoice') {
    return '/selling/invoices/create'
  }
  if (documentType === 'sell_contract') {
    return '/selling/sell-contracts/create'
  }
  return '/selling/import-documents/create'
}

const optionalId = (value: string | number | null | undefined): string => {
  if (typeof value === 'number' && !Number.isNaN(value) && value !== 0) {
    return value.toString()
  }
  return optionalParam(typeof value === 'string' ? value : null)
}

interface DocumentEditPathParams {
  documentType: string
  documentId: string
  clientId?: string | number | null
  vehicleId?: string
}

export const documentEditPath = ({
  documentType,
  documentId,
  clientId,
  vehicleId
}: DocumentEditPathParams): string | null => {
  const client = optionalId(clientId)
  if (client.length === 0 || documentId.length === 0 || !isVehicleDocumentType(documentType)) {
    return null
  }
  let path = ''
  if (documentType === 'invoice') {
    path = `/selling/clients/${client}/invoices/${documentId}/edit`
  } else if (documentType === 'import_document') {
    path = `/selling/clients/${client}/import-documents/${documentId}/edit`
  } else {
    path = `/selling/clients/${client}/sell-contracts/${documentId}/edit`
  }
  if (typeof vehicleId === 'string' && vehicleId.length > 0) {
    return appendQueryParams(path, new URLSearchParams({ vehicleId }))
  }
  return path
}

export const formatDocumentDate = (value: string): string => {
  if (value.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`
  }
  return value
}

export const vehicleDocumentCreatePath = ({ documentType, clientId, vehicleId }: DocumentCreatePathParams): string => {
  const params = new URLSearchParams()
  if (typeof clientId === 'string' && clientId.length > 0) {
    params.set('clientId', clientId)
  }
  if (typeof vehicleId === 'string' && vehicleId.length > 0) {
    params.set('vehicleId', vehicleId)
  }
  return appendQueryParams(documentCreateBasePath(documentType), params)
}

interface DocumentCreateQuery {
  pathClientId?: string
  search: string
}

interface DocumentCreateIds {
  clientId?: string
  vehicleId?: string
}

interface DocumentCreateHookParams {
  clientId?: string
  vehicleId?: string
  returnPath: string
}

export const getDocumentCreateIds = ({ pathClientId, search }: DocumentCreateQuery): DocumentCreateIds => {
  const searchParams = new URLSearchParams(search)
  const queryClientId = optionalParam(searchParams.get('clientId'))
  const pathId = optionalParam(pathClientId)
  const clientId = queryClientId.length > 0 ? queryClientId : pathId
  const vehicleId = optionalParam(searchParams.get('vehicleId'))
  return {
    ...(clientId.length > 0 ? { clientId } : {}),
    ...(vehicleId.length > 0 ? { vehicleId } : {})
  }
}

export const documentCreateReturnPath = (clientId?: string, vehicleId?: string): string => {
  if (typeof vehicleId === 'string' && vehicleId.length > 0) {
    return `/selling/vehicles/${vehicleId}`
  }
  if (typeof clientId === 'string' && clientId.length > 0) {
    return `/selling/clients/${clientId}`
  }
  return '/selling/clients'
}

export const parseOptionalNumericId = (value: string | number | undefined): number => {
  if (typeof value === 'number' && !Number.isNaN(value) && value !== 0) {
    return value
  }
  if (typeof value === 'string' && value.length > 0) {
    const parsed = parseInt(value, 10)
    if (!Number.isNaN(parsed) && parsed !== 0) {
      return parsed
    }
  }
  return 0
}

export const useDocumentCreateParams = (): DocumentCreateHookParams => {
  const { clientId: pathClientId } = useParams<{ clientId?: string }>()
  const { search } = useLocation()
  const { clientId, vehicleId } = getDocumentCreateIds({ pathClientId, search })
  return {
    clientId,
    vehicleId,
    returnPath: documentCreateReturnPath(clientId, vehicleId)
  }
}
